"use client";

import React, { useCallback } from "react";
import { useRouter } from "next/navigation";
import { CompanyDetailsTab as ReusableCompanyDetailsTab } from "@/components/shared/detail-tabs/CompanyDetailsTab";
import { CompanyContacts } from "@/features/companies/components/CompanyContacts";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Time_Entries } from "@/src/gql/graphql";

type ExtEntry = Time_Entries & {
  companies?: any | null;
  tickets?: {
    id?: string;
    company_id?: string | null;
    companies?: any | null;
  } | null;
};

/**
 * Time Entry Company Details Tab Component
 *
 * Wraps the reusable CompanyDetailsTab with time entry-specific context and data resolution.
 * Also displays the list of contacts at the company.
 */
export function CompanyDetailsTab({
  item,
  embedded,
}: TabContentProps<Time_Entries> & { embedded?: boolean }) {
  const router = useRouter();
  const ext = item as ExtEntry;

  const handleViewFullCompany = useCallback(
    (companyId: string) => {
      router.push(`/companies/${companyId}`);
    },
    [router]
  );

  const companyId = ext.company_id || ext.tickets?.company_id;

  return (
    <div className="space-y-6">
      <ReusableCompanyDetailsTab
        companyId={ext.company_id}
        companyData={ext.companies}
        companyName={ext.company_name}
        embedded={embedded}
        onViewFullCompany={handleViewFullCompany}
        nestedCompanyData={ext.tickets?.companies}
        nestedCompanyId={ext.tickets?.company_id}
        ticketId={ext.ticket_id}
      />

      {companyId && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Contacts at this Company</h3>
          <CompanyContacts companyId={companyId} />
        </div>
      )}
    </div>
  );
}
